/**
 * Reference files (📎) of the conversation on screen: chat-only context that
 * is not analyzed; its extracted text is added to the AI's conversation
 * history on every turn.
 *
 * - Chips are stored with the session they were loaded for and only shown
 *   while that session is on screen, so switching conversations never shows
 *   the previous session's chips, and a slow list response for the old
 *   session is dropped (CHAT-5).
 * - Attaching in "New chat" mode goes through `ensureSession`, which creates
 *   the session and puts it on screen (CHAT-3).
 * - The API reports `truncated` when a long file was cut to the context
 *   budget; we tell the user (D24).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as DocumentPicker from 'expo-document-picker';

import { chatAPI } from '../../services/api';
import { trackAuthEvent } from '../../services/analytics';
import { showDialog } from '@/utils/dialogs';
import { actionErrorMessage, referenceAttachErrorMessage } from './chatErrors';
import type { ConversationGuard } from './conversationGuard';
import type { ReferenceFileChip } from './components/ChatComposer';

type TFn = (key: string, defaultOrVars?: string | Record<string, unknown>, vars?: Record<string, unknown>) => string;

const NO_FILES: ReferenceFileChip[] = [];

const REFERENCE_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'application/rtf',
  'text/rtf',
  'image/png',
  'image/jpeg',
];

export function useReferenceFiles({
  guard,
  currentSessionId,
  ensureSession,
  t,
}: {
  guard: ConversationGuard;
  currentSessionId: number | null;
  /** Session to attach to; creates one in "New chat" mode. */
  ensureSession: () => Promise<number>;
  t: TFn;
}) {
  const [chips, setChips] = useState<{ sessionId: number | null; files: ReferenceFileChip[] }>({
    sessionId: null,
    files: NO_FILES,
  });
  const chipsRef = useRef(chips);
  chipsRef.current = chips;
  const [attaching, setAttaching] = useState(false);
  const attachingRef = useRef(false);
  const loadSeqRef = useRef(0);
  const ensureSessionRef = useRef(ensureSession);
  ensureSessionRef.current = ensureSession;

  const load = useCallback(async (sessionId: number | null) => {
    loadSeqRef.current += 1;
    const seq = loadSeqRef.current;
    if (!sessionId) {
      setChips({ sessionId: null, files: NO_FILES });
      return;
    }
    const isLatest = () => seq === loadSeqRef.current && guard.isViewing(sessionId);
    try {
      const data = await chatAPI.listContextFiles(sessionId);
      if (!isLatest()) return;
      setChips({
        sessionId,
        files: data.items.map((it) => ({
          message_id: it.message_id,
          filename: it.filename,
          char_count: it.char_count,
        })),
      });
    } catch {
      if (isLatest()) setChips({ sessionId, files: NO_FILES });
    }
  }, [guard]);

  useEffect(() => {
    load(currentSessionId);
  }, [currentSessionId, load]);

  const upload = useCallback(async (file: File | { uri: string; name: string; type?: string }) => {
    attachingRef.current = true;
    setAttaching(true);
    try {
      const sessionId = await ensureSessionRef.current();
      const response = await chatAPI.attachContextFile(sessionId, file);
      if (guard.isViewing(sessionId)) {
        setChips((prev) => ({
          sessionId,
          files: [
            ...(prev.sessionId === sessionId ? prev.files : NO_FILES).filter((ref) => ref.message_id !== response.message_id),
            { message_id: response.message_id, filename: response.filename, char_count: response.char_count },
          ],
        }));
        // Re-read the list so an earlier in-flight load can't drop the chip.
        void load(sessionId);
      }
      if (response.truncated) {
        showDialog(t('chat.referenceTruncated'));
      }
    } catch (err) {
      showDialog(referenceAttachErrorMessage(t, err));
    } finally {
      attachingRef.current = false;
      setAttaching(false);
    }
  }, [guard, load, t]);

  /** Paperclip menu: pick a file, then attach it. */
  const attachReferenceFile = useCallback(async () => {
    trackAuthEvent('chat_reference_attach_clicked', { source: 'header_button' });
    if (attachingRef.current) return;

    let result: DocumentPicker.DocumentPickerResult;
    try {
      result = await DocumentPicker.getDocumentAsync({ type: REFERENCE_FILE_TYPES, copyToCacheDirectory: true });
    } catch {
      showDialog(t('chat.filePickError'));
      return;
    }
    if (result.canceled || !result.assets?.[0]) return;

    const asset = result.assets[0];
    await upload(
      (asset as any).file
        ? ((asset as any).file as File)
        : { uri: asset.uri, name: asset.name, type: asset.mimeType },
    );
  }, [t, upload]);

  /** Drag-and-drop: we already have the File. */
  const attachReferenceFromFile = useCallback(async (file: File) => {
    if (attachingRef.current) return;
    await upload(file);
  }, [upload]);

  const removeReferenceFile = useCallback(async (messageId: number) => {
    const sessionId = currentSessionId;
    const before = chipsRef.current;
    setChips({ ...before, files: before.files.filter((r) => r.message_id !== messageId) });
    try {
      await chatAPI.removeContextFile(messageId);
    } catch (err) {
      // Roll back on failure (only if the same session is still on screen).
      if (guard.isViewing(sessionId)) setChips(before);
      showDialog(actionErrorMessage(t, err, 'chat.referenceRemoveFailed'));
    }
  }, [currentSessionId, guard, t]);

  return {
    referenceFiles: chips.sessionId != null && chips.sessionId === currentSessionId ? chips.files : NO_FILES,
    attachingReference: attaching,
    attachReferenceFile,
    attachReferenceFromFile,
    removeReferenceFile,
  };
}
